// ==============================================
// Admin › Map Tiles
// ==============================================
//
// Every map tile the tile front door served this month, per customer and source,
// viewed live or downloaded (docs/planning/TILE_FRONT_DOOR_PLAN.md §3.3).
//
//   Used     — tiles served to the customer: what they are charged for. The customer
//              sees exactly these figures on Billing & Plan (same API service).
//   Fetched  — tiles we asked a provider for: what we pay for. The rest came from our
//              own caches, and "Cache saved" is that share.
//
// Data: GET /api/admin/tiles/usage?month=YYYY-MM.
// Readability mandate: nothing under 14px, primary copy 16px+.

import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { providerForSource } from '@vortex/basemaps';
import { api } from '../../services/api';
import type { TilePair } from '../../types/subscription';

const fmt = (n: number) => n.toLocaleString();
const saved = (p: TilePair) => (p.served > 0 ? `${Math.round(((p.served - p.fetched) / p.served) * 100)}%` : '–');
const label = (source: string) => providerForSource(source)?.label ?? source;

function lastMonths(count: number): { value: string; label: string }[] {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    return { value: d.toISOString().slice(0, 7), label: d.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric', timeZone: 'UTC' }) };
  });
}

function Stat({ label: name, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
      <p className="text-sm text-slate-400 mb-1">{name}</p>
      <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
      <p className="text-sm text-slate-400 mt-1">{note}</p>
    </div>
  );
}

const num = 'px-4 py-3 text-right tabular-nums';

export function AdminTilesPage() {
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth] = useState(months[0].value);
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tiles', month],
    queryFn: () => api.adminGetTileUsage(month),
  });

  const { data: check } = useQuery({
    queryKey: ['admin', 'tiles', 'reconciliation'],
    queryFn: () => api.adminGetTileReconciliation(14),
  });

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Square3Stack3DIcon className="w-6 h-6 text-cyan-400" /> Map Tiles
          </h1>
          <p className="text-[16px] text-slate-400 max-w-3xl">
            Every tile served through the tile front door, viewed live or downloaded. <b className="text-slate-200">Used</b> is
            what the customer is charged for and sees on Billing &amp; Plan; <b className="text-slate-200">Fetched</b> is what we paid a provider for.
          </p>
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-[15px] text-white"
          aria-label="Month"
        >
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      {isLoading || !data ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <section aria-label="Month totals" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Tiles used" value={fmt(data.total.served)} note="Served to customers" />
            <Stat label="Fetched from providers" value={fmt(data.total.fetched)} note="What we pay for" />
            <Stat label="Served from our caches" value={fmt(data.total.served - data.total.fetched)} note="Not paid again" />
            <Stat label="Cache saved" value={saved(data.total)} note="Share of tiles not fetched" />
          </section>

          {data.users.length === 0 ? (
            <p className="card text-[16px] text-slate-300">No tiles counted at the front door this month.</p>
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-[15px]">
                <thead>
                  <tr className="text-slate-400 text-sm border-b border-slate-700">
                    <th className="text-left font-medium px-4 py-3">Customer</th>
                    <th className="text-right font-medium px-4 py-3">Live</th>
                    <th className="text-right font-medium px-4 py-3">Downloaded</th>
                    <th className="text-right font-medium px-4 py-3">Used</th>
                    <th className="text-right font-medium px-4 py-3">Fetched</th>
                    <th className="text-right font-medium px-4 py-3">Cache saved</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => {
                    const expanded = open === u.userId;
                    return (
                      <Fragment key={u.userId}>
                        <tr className="border-t border-slate-700/60">
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setOpen(expanded ? null : u.userId)}
                              aria-expanded={expanded}
                              className="flex items-start gap-2 text-left"
                            >
                              {expanded
                                ? <ChevronDownIcon className="w-4 h-4 mt-1 text-slate-400 shrink-0" />
                                : <ChevronRightIcon className="w-4 h-4 mt-1 text-slate-400 shrink-0" />}
                              <span>
                                {u.name && <span className="block text-white">{u.name}</span>}
                                <span className={u.name ? 'block text-sm text-slate-400' : 'block text-white'}>{u.email ?? u.userId}</span>
                              </span>
                            </button>
                          </td>
                          <td className={`${num} text-slate-200`}>{fmt(u.live.served)}</td>
                          <td className={`${num} text-slate-200`}>{fmt(u.download.served)}</td>
                          <td className={`${num} text-white font-semibold`}>{fmt(u.total.served)}</td>
                          <td className={`${num} text-amber-300`}>{fmt(u.total.fetched)}</td>
                          <td className={`${num} text-slate-200`}>{saved(u.total)}</td>
                        </tr>
                        {expanded && u.sources.map((s) => (
                          <tr key={s.source} className="bg-slate-900/40">
                            <td className="px-4 py-2 pl-10 text-slate-300">{label(s.source)}</td>
                            <td className={`${num} py-2 text-slate-300`}>{fmt(s.live.served)}</td>
                            <td className={`${num} py-2 text-slate-300`}>{fmt(s.download.served)}</td>
                            <td className={`${num} py-2 text-slate-200`}>{fmt(s.total.served)}</td>
                            <td className={`${num} py-2 text-amber-300/90`}>{fmt(s.total.fetched)}</td>
                            <td className={`${num} py-2 text-slate-300`}>{saved(s.total)}</td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {check && (
        <section aria-label="Esri check" className="card p-0 overflow-x-auto">
          <div className="px-4 pt-4 pb-3">
            <h2 className="text-lg font-bold text-white">Esri check</h2>
            <p className="text-[15px] text-slate-400 max-w-3xl mt-1">
              What Esri billed us each day, against what we can account for: tiles the front door fetched from Esri, and Esri
              tiles Scouts report fetching themselves. A day is red when more than 5% of the bill, and over 1,000 tiles, is
              unexplained. Until the front door holds our Esri key, its tiles are not on the bill.
            </p>
          </div>
          {check.days.length === 0 ? (
            <p className="px-4 pb-4 text-[15px] text-slate-300">Nothing to compare in the last 14 days.</p>
          ) : (
            <table className="w-full text-[15px]">
              <thead>
                <tr className="text-slate-400 text-sm border-y border-slate-700">
                  <th className="text-left font-medium px-4 py-3">Day (UTC)</th>
                  <th className="text-right font-medium px-4 py-3">Esri billed</th>
                  <th className="text-right font-medium px-4 py-3">Front door</th>
                  <th className="text-right font-medium px-4 py-3">Scouts</th>
                  <th className="text-right font-medium px-4 py-3">Unexplained</th>
                </tr>
              </thead>
              <tbody>
                {check.days.map((d) => (
                  <tr key={d.date} className="border-t border-slate-700/60">
                    <td className="px-4 py-3 text-white">{new Date(`${d.date}T00:00:00Z`).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</td>
                    <td className={`${num} text-slate-200`}>{fmt(d.billed)}</td>
                    <td className={`${num} text-slate-200`}>{fmt(d.frontDoor)}</td>
                    <td className={`${num} text-slate-200`}>{fmt(d.scouts)}</td>
                    <td className={`${num} ${d.flagged ? 'text-red-400 font-semibold' : 'text-slate-200'}`}>
                      {fmt(d.unexplained)}{d.flagged && d.billed > 0 ? ` (${Math.round((d.unexplained / d.billed) * 100)}%)` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
